import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { fetchCapitalAllocationProposal } from '../../../portfolio_decision/capital_allocation_proposal/services/capitalAllocationProposalApi'
import BoardDecisionForm from '../components/BoardDecisionForm'
import BoardDecisionReceipt from '../components/BoardDecisionReceipt'
import SubmittedProposalReview from '../components/SubmittedProposalReview'
import { createInvestmentProgramFromBoardDecision } from '../../../execution_governance/investment_program/services/investmentProgramApi'
import { CapabilityGate } from '../../../../access/AccessContext'
import { PERMISSIONS } from '../../../../access/capabilities'
import { approveBoardAnalyticalEvidence, createPendingBoardDecision, fetchBoardDecision, linkBoardAnalyticalEvidence, publishBoardAnalyticalEvidence, recordBoardDecision } from '../services/boardDecisionApi'

const errorMessage = (error) => {
  if (error.response?.status === 403) return 'You do not have permission to perform this Board governance action.'
  const data = error.response?.data
  if (typeof data?.detail === 'string') return data.detail
  if (data && typeof data === 'object') return Object.entries(data).map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(' ') : messages}`).join(' ')
  return 'The Board decision request could not be completed.'
}

export default function BoardDecisionWorkspace() {
  const { decisionReference, proposalId, version } = useParams()
  const navigate = useNavigate()
  const [state, setState] = useState({ loading: true, proposal: null, decision: null, error: '', recordingError: '' })
  const [submitting, setSubmitting] = useState(false)
  const [handoffLoading, setHandoffLoading] = useState(false)
  const [handoffError, setHandoffError] = useState('')
  const activeDecision = useRef(null)
  const handoffInFlight = useRef(false)

  useEffect(() => {
    let current = true
    const load = async () => {
      try {
        const decision = decisionReference ? await fetchBoardDecision(decisionReference) : null
        if (decision && decision.proposal_kind === 'INVESTMENT_PROPOSAL' && (decision.status === 'RECORDED' || decision.status === 'SUPERSEDED')) {
          if (current) { activeDecision.current = decision; setState({ loading: false, proposal: null, decision, error: '', recordingError: '' }) }
          return
        }
        const exactId = decision?.proposal_id || proposalId
        const exactVersion = decision?.proposal_version || version
        const proposal = await fetchCapitalAllocationProposal({ proposalId: exactId, version: exactVersion })
        if (current) { activeDecision.current = decision; setState({ loading: false, proposal, decision, error: '', recordingError: '' }) }
      } catch (error) { if (current) setState((value) => ({ ...value, loading: false, error: errorMessage(error) })) }
    }
    load()
    return () => { current = false }
  }, [decisionReference, proposalId, version])

  const record = async (payload) => {
    if (submitting) return
    setSubmitting(true); setState((value) => ({ ...value, recordingError: '' }))
    try {
      let pending = activeDecision.current
      if (!pending) {
        pending = await createPendingBoardDecision({ proposal_id: state.proposal.proposal_id, proposal_version: state.proposal.version })
        activeDecision.current = pending
      }
      const recorded = await recordBoardDecision({ decisionReference: pending.decision_reference, payload })
      setState((value) => ({ ...value, decision: recorded }))
      navigate(`/board-decisions/${recorded.decision_reference}`, { replace: true })
    } catch (error) { setState((value) => ({ ...value, recordingError: errorMessage(error) })) }
    finally { setSubmitting(false) }
  }

  const evidenceAction = async () => {
    if (handoffInFlight.current) return
    handoffInFlight.current = true; setHandoffLoading(true); setHandoffError('')
    try {
      const evidence = state.decision.analytical_evidence_state
      if (evidence.status === 'REQUIRED') await publishBoardAnalyticalEvidence(state.decision.decision_reference)
      else if (evidence.status === 'AWAITING_APPROVAL') await approveBoardAnalyticalEvidence(evidence.portfolio_snapshot_id)
      else if (evidence.status === 'APPROVED_UNLINKED') await linkBoardAnalyticalEvidence(state.decision.decision_reference, evidence.portfolio_snapshot_id)
      const decision = await fetchBoardDecision(state.decision.decision_reference)
      activeDecision.current = decision; setState((value) => ({ ...value, decision }))
    } catch (error) { setHandoffError(errorMessage(error)) }
    finally { handoffInFlight.current = false; setHandoffLoading(false) }
  }

  const handoff = async () => {
    if (handoffInFlight.current || !state.decision?.decision_reference) return
    handoffInFlight.current = true
    setHandoffLoading(true)
    setHandoffError('')
    try {
      const program = await createInvestmentProgramFromBoardDecision({ boardDecisionReference: state.decision.decision_reference })
      navigate(`/investment-programs/${program.program_reference}`, { state: { investmentProgramHandoff: true } })
    } catch (error) {
      setHandoffError(errorMessage(error))
    } finally {
      handoffInFlight.current = false
      setHandoffLoading(false)
    }
  }

  if (state.loading) return <main className="min-h-full bg-slate-50 p-8"><div role="status" className="rounded-xl bg-white p-6 shadow-sm">Loading exact proposal version…</div></main>
  if (state.error) return <main className="min-h-full bg-slate-50 p-8"><div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-800">{state.error}</div></main>
  if (state.decision?.status === 'RECORDED' || state.decision?.status === 'SUPERSEDED') return <main className="min-h-full bg-slate-50 p-4 sm:p-8"><BoardDecisionReceipt decision={state.decision} onCreateInvestmentProgram={handoff} onEvidenceAction={evidenceAction} handoffLoading={handoffLoading} handoffError={handoffError} /></main>

  return <main className="min-h-full bg-slate-50 p-4 sm:p-8"><SubmittedProposalReview proposal={state.proposal} /><div aria-hidden="true" className="my-6 flex items-center justify-center gap-3 text-sm font-semibold text-slate-500"><span>Management Submission</span><span className="text-xl">↓</span><span>Board Determination</span></div><CapabilityGate permission={PERMISSIONS.board.record}><BoardDecisionForm onSubmit={record} submitting={submitting} error={state.recordingError} /></CapabilityGate></main>
}
